/**
 * PATH: apps/quote/src/components/builder/scope-ag-grid/scope-ag-grid-formatters.ts
 * FILE_ID: QB-AGGRID-FMT-001
 * PURPOSE: Pure formatting + computed-value resolvers used by the AG-Grid
 *          column definitions. Split out from scope-ag-grid-columns.ts so
 *          that file stays under the 300-line cap and the formatters are
 *          independently testable.
 *
 * RELATED:
 *   - QB-AGGRID-COLUMNS-001 (scope-ag-grid-columns.ts) — consumer
 *   - QUOTE-BUILDER-MATH-COMPUTE-001 (computeLine.ts) — derivative source
 */

import type { QuoteLineItem } from '@/lib/quoteApi'
import { computeLine } from '@/lib/quote-builder-math/computeLine'
import {
  getSlotFieldValue,
  parseSlotFieldId,
} from './bravada-material-slots'

/** Coerce string|number|null|undefined to number; returns 0 on garbage. */
export function coerceNumber(v: unknown): number {
  if (v === null || v === undefined || v === '') return 0
  if (typeof v === 'number') return Number.isFinite(v) ? v : 0
  const n = Number.parseFloat(String(v))
  return Number.isFinite(n) ? n : 0
}

/** Currency formatter for display — matches the legacy grid's "$1,234.56". */
export function fmtCurrency(value: unknown): string {
  if (value === null || value === undefined || value === '') return ''
  const n = coerceNumber(value)
  if (!Number.isFinite(n) || n === 0) return ''
  return n.toLocaleString('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

/** Plain-number formatter — used for hours/days/qty etc. */
export function fmtNumber(value: unknown, digits = 2): string {
  if (value === null || value === undefined || value === '') return ''
  const n = coerceNumber(value)
  if (!Number.isFinite(n)) return ''
  return n.toLocaleString('en-AU', {
    minimumFractionDigits: 0,
    maximumFractionDigits: digits,
  })
}

/** Percent formatter — outcome margin column. */
export function fmtPercent(value: unknown): string {
  if (value === null || value === undefined || value === '') return ''
  const n = coerceNumber(value)
  if (!Number.isFinite(n)) return ''
  return `${n.toFixed(1)}%`
}

/**
 * Currency-typed column ids — drives the agNumberCellEditor + currency
 * valueFormatter selection in scope-ag-grid-columns.ts. Custom columns
 * route through the column's own `type` property instead.
 */
export const CURRENCY_FIELDS: ReadonlySet<string> = new Set([
  'labour_chargeout',
  'labour_cost',
  'material_chargeout',
  'material_cost',
  'plant_chargeout',
  'plant_cost',
  'plant_unit_cost',
  'subcon_chargeout',
  'subcon_cost',
  'subcon_quote',
  'rate_per_unit',
  'adjusted_rate',
  'subtotal',
  'subtotal_excl_gst',
  'total_cost',
  'chargeout_total',
  'margin',
  // Bravada PRODUCTS aggregate columns (Excel cols AM-AT)
  'material_total_cost',
  'material_cost_per_m2',
  'material_total_with_wastage',
  'material_markup_amount',
  'material_charge_out',
  'material_charge_out_per_m2',
  // Plant Rental markup $ (Excel col AZ)
  'plant_markup_amount',
  // Subcontractor markup $ (Excel col BG)
  'subcon_markup_amount',
  // Bravada PRODUCTS slot sub-bands (Excel cols AB-AL).
  // Per-slot unit costs and totals are currency; coverage stays
  // numeric (handled by NUMERIC_FIELDS below).
  'primer_unit_cost',
  'primer_total_cost',
  'basecoat_unit_cost',
  'basecoat_total_cost',
  'topcoat_unit_cost',
  'topcoat_total_cost',
  'accessories_unit_cost',
  'accessories_total_cost',
])

/** Plain-numeric column ids (no currency, no percent). */
export const NUMERIC_FIELDS: ReadonlySet<string> = new Set([
  'quantity',
  'productivity_pmpd',
  'labour_hours',
  'labour_days',
  'plant_quantity',
  'subcon_days',
  // Bravada per-slot coverage (m², LM/unit). Excel cols AC / AF / AI.
  // Accessories has no coverage column so not listed here.
  'primer_coverage',
  'basecoat_coverage',
  'topcoat_coverage',
])

/** Percent-typed column ids. */
export const PERCENT_FIELDS: ReadonlySet<string> = new Set([
  'margin_percent',
  // Bravada per-discipline markup / wastage percentages.
  'material_wastage_percent',
  'material_markup_percent',
  'plant_markup_percent',
  'subcon_markup_percent',
  // MR H: per-line OVERRIDES band. The backend stores fractions
  // (0.10) but the user types percents (10) — being in this set
  // routes them through the percent normalisation in the persist
  // handler so we save the correct decimal.
  'override_material_markup',
  'override_material_wastage',
  'override_plant_markup',
  'override_subcon_markup',
  'override_downtime',
])

/**
 * Compute `markup amount` = chargeout - cost. Pure, side-effect-free.
 * Returns null when both inputs are zero so the cell renders blank
 * (matches Excel's behaviour where empty plant/subcon rows show
 * nothing rather than $0.00).
 */
function markupAmount(cost: unknown, chargeout: unknown): number | null {
  const c = coerceNumber(cost)
  const co = coerceNumber(chargeout)
  if (c === 0 && co === 0) return null
  return co - c
}

/**
 * Compute `markup percent` = (chargeout - cost) / cost * 100.
 * Returns null when cost is zero (avoids div-by-zero, matches Excel
 * `#DIV/0!` cells which Bravada estimators leave blank).
 */
function markupPercent(cost: unknown, chargeout: unknown): number | null {
  const c = coerceNumber(cost)
  const co = coerceNumber(chargeout)
  if (c === 0) return null
  return ((co - c) / c) * 100
}

/**
 * Compute a per-unit ratio (e.g. cost per m²). Returns null when the
 * divisor is zero so the cell renders blank.
 */
function perUnit(value: unknown, qty: unknown): number | null {
  const v = coerceNumber(value)
  const q = coerceNumber(qty)
  if (q === 0) return null
  return v / q
}

/**
 * Resolve the value of a "computed" column for a row. Mirrors the legacy
 * grid's behaviour: live-compute via computeLine for the canonical
 * derivatives, fall back to the stored value for fields we haven't
 * wired explicitly.
 *
 * The Bravada per-discipline markup / per-m² resolvers below are
 * pure derivations of the stored cost + chargeout (no quote.config
 * dependency) — keeps the call sig narrow and the math testable.
 */
export function getComputedValue(
  data: QuoteLineItem | null | undefined,
  fieldId: string,
): unknown {
  if (!data) return null
  const d = data as unknown as Record<string, unknown>

  // Slot-prefixed field ids (primer_*, basecoat_*, topcoat_*,
  // accessories_*) read from QuoteLineItem.materials[slot=<slot>].
  // Handled first so the slot resolvers can short-circuit before the
  // canonical computeLine() runs (which is more expensive).
  if (parseSlotFieldId(fieldId)) {
    return getSlotFieldValue(data, fieldId)
  }

  const computed = computeLine(data)
  switch (fieldId) {
    case 'item_number':
      return data.item_number ?? null
    case 'labour_hours':
      return computed.labour_hours
    case 'labour_days':
      return computed.labour_days
    case 'rate_per_unit':
      return computed.rate_per_unit
    case 'chargeout_total':
      return computed.chargeout_total
    case 'total_cost':
      return computed.total_cost
    case 'margin':
      return computed.margin
    case 'margin_percent':
      return computed.margin_percent
    case 'subtotal':
      return data.subtotal ?? computed.chargeout_total
    // Excel col R "Sub Total (excl GST)" — same value as `subtotal`
    // (Excel duplicates the cell for the estimator's eye).
    case 'subtotal_excl_gst':
      return data.subtotal ?? computed.chargeout_total

    // ----- Bravada PRODUCTS aggregate columns -----
    // Excel col AM — band total cost (re-render of material_cost).
    case 'material_total_cost':
      return data.material_cost ?? null
    // Excel col AN — wastage % override (display per-line value only;
    // null means "engine falls back to quote.config.materialWastage").
    case 'material_wastage_percent': {
      const v = d.override_material_wastage
      // Stored as a fraction (0.075) in the DB; display as percent.
      if (v === null || v === undefined) return null
      return coerceNumber(v) * 100
    }
    // Excel col AO — cost / m² derivation.
    case 'material_cost_per_m2':
      return perUnit(data.material_cost, data.quantity)
    // Excel col AP — total + wastage. When override is unset we just
    // show the raw cost (engine will apply default at save).
    case 'material_total_with_wastage': {
      const cost = coerceNumber(data.material_cost)
      const w = d.override_material_wastage
      if (w === null || w === undefined) return cost || null
      return cost * (1 + coerceNumber(w))
    }
    // Excel col AQ — markup % override.
    case 'material_markup_percent': {
      const v = d.override_material_markup
      if (v === null || v === undefined) {
        // Derive from cost + chargeout when override unset.
        return markupPercent(data.material_cost, data.material_chargeout)
      }
      return coerceNumber(v) * 100
    }
    case 'material_markup_amount':
      return markupAmount(data.material_cost, data.material_chargeout)
    // Excel col AS — chargeout re-render.
    case 'material_charge_out':
      return data.material_chargeout ?? null
    case 'material_charge_out_per_m2':
      return perUnit(data.material_chargeout, data.quantity)

    // ----- Plant Rental markup columns (Excel cols AY-AZ) -----
    case 'plant_markup_percent': {
      const v = d.override_plant_markup
      if (v === null || v === undefined) {
        return markupPercent(data.plant_cost, data.plant_chargeout)
      }
      return coerceNumber(v) * 100
    }
    case 'plant_markup_amount':
      return markupAmount(data.plant_cost, data.plant_chargeout)

    // ----- Subcontractor markup columns (Excel cols BF-BG) -----
    case 'subcon_markup_percent': {
      const v = d.override_subcon_markup
      if (v === null || v === undefined) {
        return markupPercent(data.subcon_cost, data.subcon_chargeout)
      }
      return coerceNumber(v) * 100
    }
    case 'subcon_markup_amount':
      return markupAmount(data.subcon_cost, data.subcon_chargeout)

    // ----- MR H: per-line OVERRIDES band -----
    // All five override fields store decimal fractions in the DB
    // (0.10 = 10%) and display as percentages so estimators don't have
    // to type "0.075" — they type "7.5". The reverse conversion lives
    // in useScopeAgPersistHandlers when a cell edit lands.
    case 'override_material_markup':
    case 'override_material_wastage':
    case 'override_plant_markup':
    case 'override_subcon_markup':
    case 'override_downtime': {
      const v = (d as unknown as Record<string, unknown>)[fieldId]
      if (v === null || v === undefined || v === '') return null
      return coerceNumber(v) * 100
    }

    default:
      return (data as unknown as Record<string, unknown>)[fieldId]
  }
}
